import { Car, Users } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { APPLICANT_TONES, useApplicantPositions, useApplicants } from '@/hooks/applicants';
import { formatInstant } from '@/lib/dates';
import {
  APPLICATION_STATUS_FILTERS,
  type ApplicantListItem,
  type ApplicationStatusValue,
} from '@shared/schemas/applicant';
import { DEFAULT_PAGE_SIZE, RECORD_SORTS } from '@shared/schemas/pagination';
import { SortSelect } from '@/components/ui/sort-select';
import type { RecordSort } from '@shared/schemas/pagination';

/*
 * /admin/applicants — ROADMAP.md §9.10, and the first screen to read
 * `job_application`.
 *
 * **The careers form has been live and public with nothing reading it.** A real
 * application was stored perfectly and was invisible to the business through
 * every route it had. This page is the fix, and the ordering rule follows from
 * it: New and Reviewing list oldest first, because there is a person waiting at
 * the other end and a queue is worked from the front.
 *
 * Cards rather than a table, for the reason the job-request queue is: each row
 * carries several facts and an action, which is the shape a table clips on a
 * phone.
 *
 * The list deliberately shows **less than the detail does**. An address and an
 * employment history are not things to render across a screen somebody leaves
 * open in an office — they are one click away, on a page you go to on purpose.
 */

export function ApplicantsPage() {
  const [status, setStatus] = useState<ApplicationStatusValue | 'all'>('new');
  const [search, setSearch] = useState('');
  const [positionId, setPositionId] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<RecordSort>('newest');

  const applicants = useApplicants({ sort, page, status, search, positionId });
  const positions = useApplicantPositions();
  const counts = applicants.data?.counts;

  const filtered = search !== '' || positionId !== '';

  return (
    <AdminPage
      title="Applicants"
      description="Everyone who has applied through the careers form, and where each one has got to."
    >
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            label="Search applicants"
            placeholder="Name, email or phone"
            className="sm:w-64"
          />

          <label className="flex items-center gap-2">
            <span className="sr-only">Filter by position applied for</span>
            <select
              name="positionId"
              value={positionId}
              onChange={(event) => {
                setPositionId(event.target.value);
                setPage(1);
              }}
              className="min-h-control rounded-sm border border-ink-300 bg-white px-3 text-body"
            >
              <option value="">All positions</option>
              {(positions.data?.positions ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="mb-4">
        <FilterPills
          label="Filter applicants by status"
          options={APPLICATION_STATUS_FILTERS.map((option) => ({
            ...option,
            ...(counts === undefined ? {} : { count: counts[option.value] }),
          }))}
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
        />
      </div>

      {/* The order control. A select rather than clickable headers —

          the list becomes cards below 768px and a card has no headers.

          Above the loading/error/list conditional, so it does not

          vanish on the two screens where somebody might reach for it. */}

      <div className="mb-3 flex justify-end">
        <SortSelect
          label="Sort applicants"

          options={RECORD_SORTS}

          value={sort}

          onChange={(next) => {
            setSort(next);

            // Page 1, or you are on page 3 of a differently ordered list.

            setPage(1);
          }}
        />
      </div>

      {applicants.error ? (
        /* A failed request is an error, never an empty state — CLAUDE.md §4, P22.
           "No applicants" under a broken query would tell an office nobody has
           applied when somebody has. */
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="The applicant pipeline could not be loaded"
            onRetry={() => {
              void applicants.refetch();
            }}
          />
        </div>
      ) : applicants.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading applicants</span>
          <div aria-hidden="true" className="h-48 animate-pulse rounded-lg bg-ink-100" />
        </div>
      ) : applicants.data && applicants.data.data.length > 0 ? (
        <ul className="space-y-3">
          {applicants.data.data.map((entry) => (
            <ApplicantCard key={entry.id} applicant={entry} />
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={Users}
            title={
              filtered
                ? 'No applicants match those filters.'
                : status === 'new'
                  ? 'Nothing new waiting on you.'
                  : 'No applications with that status.'
            }
            hint={
              filtered
                ? 'Try clearing the search or the position filter.'
                : status === 'new'
                  ? 'When somebody applies through the careers page, they land here.'
                  : 'Try another filter.'
            }
          />
        </div>
      )}

      {applicants.data ? (
        <Pagination
          page={applicants.data.page}
          pageSize={applicants.data.pageSize || DEFAULT_PAGE_SIZE}
          total={applicants.data.total}
          onPageChange={setPage}
          itemNoun="applicants"
        />
      ) : null}
    </AdminPage>
  );
}

function ApplicantCard({ applicant }: { applicant: ApplicantListItem }) {
  return (
    <li className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <span className="text-h3 text-ink-900">{applicant.fullName}</span>
            <StatusBadge label={applicant.statusLabel} tone={APPLICANT_TONES[applicant.status]} />
          </p>
          <p className="mt-0.5 text-meta text-ink-500">
            Applied {formatInstant(applicant.submittedAt)}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          <Button asChild variant="outline">
            {/* The actions live on the detail page. Reviewing somebody is
                reading their application, not clicking a button beside a name. */}
            <Link to={`/admin/applicants/${applicant.id}`}>
              {applicant.status === 'new' ? 'Review' : 'Open'}
            </Link>
          </Button>
        </div>
      </div>

      <dl className="mt-3 grid gap-x-6 gap-y-1.5 text-body text-ink-700 sm:grid-cols-2">
        <Row label="Applied for">
          {/* `position.name`, never the id (P5). */}
          {applicant.positionName ?? (
            <span className="italic text-ink-500">That position has been removed</span>
          )}
        </Row>
        <Row label="Wants">{applicant.employmentTypeLabel}</Row>
        <Row label="Cleaning experience">
          {applicant.experienceYearsLabel ?? (
            // Not a blank and not a dash. They answered — the answer was none.
            <span className="text-ink-500">None</span>
          )}
        </Row>
        <Row label="Contact">
          <span className="break-all">{applicant.email}</span>
          <span className="text-ink-500"> · {applicant.phone}</span>
        </Row>
      </dl>

      {applicant.hasReliableTransportation ? (
        /* Named on the card because in Iron County it decides whether somebody
           can be sent to a job at all — prototype-copy/careers-form.md. */
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-honey-50 px-2.5 py-0.5 text-meta text-bark-900">
          <Car aria-hidden="true" className="size-3.5" />
          Has reliable transport
        </p>
      ) : null}
    </li>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-ink-500">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}
